/* NotificationsBell — small bell icon on Profile that shows unread count
 * and opens a popover with the latest notifications from the RecipeFiler
 * team (level-ups + badges). Polls `/api/notifications/unread-count`
 * every 60s while mounted; opens a full list on click.
 */
import { useEffect, useState, useCallback } from "react";
import { Bell } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function NotificationsBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const iv = setInterval(refreshCount, 60_000);
    return () => clearInterval(iv);
  }, [refreshCount]);

  const openList = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=30");
      setItems(data.items || []);
      // Mark all read
      if ((data.unread || 0) > 0) {
        await api.post("/notifications/mark-read", {});
        setUnread(0);
      }
    } catch { toast.error("Couldn't load notifications"); }
    finally { setLoading(false); }
  };

  const removeItem = async (nid) => {
    try {
      await api.delete(`/notifications/${nid}`);
      setItems((prev) => prev.filter((n) => n.notification_id !== nid));
    } catch { toast.error("Couldn't dismiss"); }
  };

  return (
    <>
      <button
        type="button"
        data-testid="notifications-bell"
        onClick={openList}
        aria-label={`Notifications (${unread} unread)`}
        className="relative inline-flex items-center justify-center w-11 h-11 rounded-full bg-rf-cream-alt hover:bg-rf-amber/30 transition-colors"
      >
        <Bell size={22} weight="duotone" className="text-rf-ink" />
        {unread > 0 && (
          <span
            data-testid="notifications-bell-badge"
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-rf-orange text-white text-[11px] font-black flex items-center justify-center leading-none"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-end p-4 sm:p-6 bg-rf-ink/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="notifications-drawer"
        >
          <div className="bg-rf-cream rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden border-2 border-rf-amber/40">
            <div className="p-4 border-b border-rf-ink/10 flex items-center justify-between">
              <h3 className="font-display font-black text-lg text-rf-ink">Notifications</h3>
              <button
                type="button"
                data-testid="notifications-close"
                onClick={() => setOpen(false)}
                className="text-rf-ink-soft hover:text-rf-ink text-sm font-bold"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && <p className="text-center text-rf-ink-soft text-sm py-8">Loading…</p>}
              {!loading && items.length === 0 && (
                <div className="text-center py-12 px-6">
                  <div className="text-4xl mb-3" aria-hidden>🔔</div>
                  <p className="font-display font-black text-rf-ink mb-1">No notifications yet</p>
                  <p className="text-xs text-rf-ink-soft">Publish public recipes and level up — we&apos;ll ping you here.</p>
                </div>
              )}
              {items.map((n) => (
                <div
                  key={n.notification_id}
                  data-testid={`notification-${n.kind}`}
                  className={`p-4 border-b border-rf-ink/5 hover:bg-rf-cream-alt/50 ${!n.read ? "bg-rf-amber/10" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-black text-sm text-rf-ink mb-1">{n.title}</p>
                      <p className="text-xs text-rf-ink-soft leading-relaxed">{n.body}</p>
                      <p className="text-[10px] text-rf-ink-soft/70 mt-2 uppercase tracking-wider font-bold">
                        {new Date(n.created_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(n.notification_id)}
                      aria-label="Dismiss"
                      className="text-rf-ink-soft hover:text-red-600 text-xs font-bold shrink-0"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
